/** Shared bits for the Discover pages: market picker, number formatting, the strategist read card. */
import { Loader2, Sparkles } from 'lucide-react';
import { MarketPicker } from '@/components/MarketPicker';

export const compact = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(Math.round(n || 0)));
export const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');
export const card = 'rounded-2xl border border-[#eaecf0] bg-white';

/** `market` is the list name ("Sweden"); `onlyCodes` limits the picker (e.g. the markets TikTok ranks). */
export function PageHeader({ title, blurb, market, onMarket, right, onlyCodes }: { title: string; blurb: string; market: string; onMarket: (name: string) => void; right?: React.ReactNode; onlyCodes?: readonly string[] }) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0"><h1 className="text-[24px] text-[#101828]" style={{ fontWeight: 700 }}>{title}</h1><p className="mt-1 max-w-2xl text-[13px] text-[#667085]">{blurb}</p></div>
      <div className="flex items-center gap-2">
        <MarketPicker value={market} onChange={onMarket} onlyCodes={onlyCodes} className="min-w-[10rem]" data-testid="discover-market" />
        {right}
      </div>
    </div>
  );
}

/** The one-paragraph strategist read a client actually reads — headline plus three short lists. */
export function ReadCard({ headline, at, sections, pending, pendingText }: { headline?: string; at?: string; sections: Array<{ label: string; items: React.ReactNode[] }>; pending?: boolean; pendingText?: string }) {
  if (pending) return <div className={`${card} mb-5 flex items-center gap-2 p-4 text-[13px] text-[#667085]`}><Loader2 className="h-4 w-4 animate-spin" /> {pendingText || 'Reading…'}</div>;
  if (!headline) return null;
  return (
    <div className="mb-5 rounded-2xl border border-[#e9d7fe] bg-gradient-to-br from-[#faf5ff] to-white p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 rounded-lg bg-[#f4ebff] p-2 text-[#be43ff]"><Sparkles className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[16px] text-[#101828]" style={{ fontWeight: 700 }}>{headline}</div>
          {at && <div className="mt-0.5 text-[11px] text-[#98a2b3]">Strategist read · {when(at)} · from the material below, nothing else</div>}
          <div className="mt-3 grid gap-4 md:grid-cols-3">
            {sections.filter((s) => s.items.length).map((s) => (
              <div key={s.label}><div className="text-[11px] font-semibold uppercase tracking-wide text-[#6941c6]">{s.label}</div><ul className="mt-1.5 space-y-1.5 text-[13px] leading-snug text-[#344054]">{s.items.map((x, i) => <li key={i} className="flex gap-2"><span className="text-[#be43ff]">•</span><span>{x}</span></li>)}</ul></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
